// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
/**
 * BusinessPage - Onderneming
 * Leest/schrijft de onderneming/KVK-velden van NotarialData via loadNotarial/saveNotarial.
 * READ-MODIFY-WRITE: de volledige notariële blob blijft in state; alleen onderneming-velden
 * worden bewerkt, zodat executeur/voogdij/bezittingen e.d. nooit worden overschreven.
 */
import { useState, useEffect } from 'react'
import type { MutableRefObject } from 'react'
import { ExternalLink, BadgeCheck } from 'lucide-react'
import { ModuleShell }     from '@/components/ui/ModuleShell'
import { SaveStatusBadge } from '@/components/ui/SaveStatusBadge'
import { Input }           from '@/components/ui/Input'
import { Select }          from '@/components/ui/Select'
import { Textarea }        from '@/components/ui/Textarea'
import { Button }          from '@/components/ui/Button'
import { Card }            from '@/components/ui/Card'
import { RegieCoachBlock } from '@/components/process/RegieCoachBlock'
import { LoadingState }    from '@/components/feedback/LoadingState'
import { useAuthStore }    from '@/store'
import { useAutosave }     from '@/hooks/useAutosave'
import { nowISO }          from '@/lib/utils'
import { loadNotarial, saveNotarial, EMPTY_NOTARIAL, type NotarialData } from '@/services/notarial'

const RECHTSVORM_OPTIONS = [
  { value: 'eenmanszaak', label: 'Eenmanszaak' },
  { value: 'zzp',         label: 'ZZP' },
  { value: 'vof',         label: 'VOF' },
  { value: 'maatschap',   label: 'Maatschap' },
  { value: 'bv',          label: 'BV' },
  { value: 'nv',          label: 'NV' },
  { value: 'stichting',   label: 'Stichting' },
  { value: 'cooperatie',  label: 'Coöperatie' },
  { value: 'overig',      label: 'Overig' },
]

export function BusinessPage({ saveRef }: { saveRef?: MutableRefObject<(() => Promise<void>) | null> } = {}) {
  const { userId } = useAuthStore()
  const [data, setData]     = useState<NotarialData | null>(null)

  useEffect(() => {
    if (!userId) return
    loadNotarial(userId).then(d => setData(d ?? EMPTY_NOTARIAL))
  }, [userId])

  const { status, saveNow } = useAutosave({
    data: data ?? EMPTY_NOTARIAL,
    onSave: async (d) => { if (userId && data) await saveNotarial(userId, d) },
    enabled: !!data,
  })

  // Wizard-embed: registreer saveNow zodat de wizard pending invoer flusht vóór navigatie
  useEffect(() => {
    if (saveRef) saveRef.current = saveNow
    return () => { if (saveRef) saveRef.current = null }
  }, [saveRef, saveNow])

  // Bewerkt ALLEEN onderneming-velden; rest van de blob blijft intact (RMW).
  function upd(field: keyof NotarialData, value: string) {
    setData(prev => (prev ? { ...prev, [field]: value } : prev))
  }

  function markKvkChecked() {
    setData(prev => (prev ? { ...prev, kvkCheckStatus: 'checked', kvkCheckDatum: nowISO() } : prev))
  }

  if (!data) return <LoadingState />

  const kvkQuery = encodeURIComponent(data.kvkNummer || data.bedrijfsnaam || '')

  return (
    <ModuleShell
      title="Onderneming"
      description="Leg de basisgegevens van uw onderneming vast zodat uw naasten weten wat er geregeld moet worden."
      actions={<SaveStatusBadge status={status} />}
    >
      <Card className="flex flex-col gap-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Input label="Bedrijfsnaam" value={data.bedrijfsnaam} onChange={e => upd('bedrijfsnaam', e.target.value)} />
          <Input label="Handelsnaam" value={data.handelsnaam} onChange={e => upd('handelsnaam', e.target.value)} />
          <Select label="Rechtsvorm" options={RECHTSVORM_OPTIONS} placeholder="Kies rechtsvorm..."
            value={data.rechtsvorm} onChange={e => upd('rechtsvorm', e.target.value)} />
          <Input label="KvK-nummer" value={data.kvkNummer} onChange={e => upd('kvkNummer', e.target.value)} placeholder="8 cijfers" />
          <Input label="BTW-nummer" value={data.btwNummer} onChange={e => upd('btwNummer', e.target.value)} />
          <Input label="Vestigingsadres" value={data.kvkVestigingsadres} onChange={e => upd('kvkVestigingsadres', e.target.value)} />
          <Input label="Website" value={data.ondernemingWebsite} onChange={e => upd('ondernemingWebsite', e.target.value)} placeholder="www.bedrijf.nl" />
          <Input label="Zakelijk e-mailadres" type="email" value={data.ondernemingEmail} onChange={e => upd('ondernemingEmail', e.target.value)} placeholder="info@bedrijf.nl" />
          <Input label="Zakelijk telefoonnummer" type="tel" value={data.ondernemingTelefoon} onChange={e => upd('ondernemingTelefoon', e.target.value)} placeholder="010-..." />
          <Input label="Zakelijke bankrekening (IBAN)" value={data.ondernemingBank} onChange={e => upd('ondernemingBank', e.target.value)} placeholder="NL00 BANK 0000 0000 00" />
        </div>

        {/* KVK-check via deep-link (geen API/CORS) */}
        <div className="flex flex-wrap items-center gap-3 pt-1">
          <a
            href={`https://www.kvk.nl/zoeken/?q=${kvkQuery}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm text-ink-2 underline underline-offset-2 hover:text-ink"
          >
            <ExternalLink className="h-4 w-4" /> Controleer in het KVK-register
          </a>
          <Button variant="ghost" onClick={markKvkChecked}>
            <BadgeCheck className="h-4 w-4" /> Markeer als gecontroleerd
          </Button>
          {data.kvkCheckStatus === 'checked' && (
            <span className="text-xs text-ink-4">Gecontroleerd op {data.kvkCheckDatum?.slice(0, 10)}</span>
          )}
        </div>
      </Card>

      <Card className="flex flex-col gap-4">
        <Textarea label="Wens voor opvolging" value={data.ondernemingWens} rows={2}
          onChange={e => upd('ondernemingWens', e.target.value)}
          placeholder="Wilt u dat de onderneming wordt voortgezet, overgedragen of afgewikkeld?" />
        <Textarea label="Afwikkeling" value={data.ondernemingAfwikkel} rows={2}
          onChange={e => upd('ondernemingAfwikkel', e.target.value)}
          placeholder="Hoe moet de onderneming worden afgewikkeld?" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Input label="Boekhouder / accountant" value={data.ondernemingBoekhouder} onChange={e => upd('ondernemingBoekhouder', e.target.value)} />
          <Input label="Beoogde opvolger" value={data.kvkOpvolger} onChange={e => upd('kvkOpvolger', e.target.value)} />
        </div>
        <Textarea label="Overige bijzonderheden" value={data.ondernemingOverig} rows={2}
          onChange={e => upd('ondernemingOverig', e.target.value)} />
        <Textarea label="Extra vestigingen / locaties" value={data.ondernemingVestigingen} rows={2}
          onChange={e => upd('ondernemingVestigingen', e.target.value)}
          placeholder="Andere vestigingsadressen of bedrijfslocaties" />
        <p className="text-xs text-ink-4">Bedrijfsvoertuigen legt u vast in de module <strong>Voertuigen &amp; mobiliteit</strong>.</p>
      </Card>

      <div className="rounded-2xl bg-blue-50 border border-blue-100 px-4 py-4">
        <p className="text-sm font-semibold text-ink mb-1">Officiële informatie bij overlijden van een ondernemer</p>
        <p className="text-xs text-ink-3 leading-relaxed mb-3">
          Bij overlijden moeten onder meer de Kamer van Koophandel en de Belastingdienst worden geïnformeerd.
          Raadpleeg de officiële bronnen voor de juiste stappen:
        </p>
        <ul className="flex flex-col gap-1.5 text-sm">
          <li>
            <a href="https://www.kvk.nl/overlijden/" target="_blank" rel="noopener noreferrer"
              className="text-sage-700 underline hover:text-sage-800">KVK - Overlijden van een ondernemer</a>
          </li>
          <li>
            <a href="https://www.belastingdienst.nl/" target="_blank" rel="noopener noreferrer"
              className="text-sage-700 underline hover:text-sage-800">Belastingdienst - aangifte en zaken na overlijden</a>
          </li>
          <li>
            <a href="https://ondernemersplein.kvk.nl/" target="_blank" rel="noopener noreferrer"
              className="text-sage-700 underline hover:text-sage-800">Ondernemersplein - bedrijf beëindigen of overdragen</a>
          </li>
        </ul>
      </div>
      <RegieCoachBlock page="business" />
    </ModuleShell>
  )
}
